/**
 * Shared hook for user's generated images.
 * Uses a global store (imagesById + orderIds) to avoid full refetches
 * and prevent heavy re-renders/freezes on delete.
 */

import { useMemo, useCallback } from "react";
import { type GeneratedImage, useGeneratedImagesStore } from "@/contexts/GeneratedImagesContext";
import type { GenerationType } from "@shared/schemas/enums";
import { useStaleGenerationCleanup } from "@/hooks/useStaleGenerationCleanup";
import { useCompletionAnimation } from "@/hooks/useCompletionAnimation";

export type { GeneratedImage } from "@/contexts/GeneratedImagesContext";

/**
 * Group images into projects (for ads) and standalone items (for director, edit, etc.)
 * Uses batch_id to group ad images from the same generation together.
 */
export function groupImagesIntoProjects(images: GeneratedImage[]): {
  projects: Map<string, GeneratedImage[]>;
  standalone: GeneratedImage[];
} {
  const isAdImage = (img: GeneratedImage) =>
    img.pipeline === "ad";

  const adImages = images.filter(isAdImage);
  const standalone = images.filter((img) => !isAdImage(img));

  // Group ad images by batch_id (fall back to individual project for legacy images without batch_id)
  const projects = new Map<string, GeneratedImage[]>();

  for (const img of adImages) {
    const key = img.batch_id ? `batch-${img.batch_id}` : `project-${img.id}`;
    const group = projects.get(key) || [];
    group.push(img);
    projects.set(key, group);
  }

  return { projects, standalone };
}

export function useGeneratedImages(generationType?: GenerationType | "all" | "images", pipeline?: string) {
  const store = useGeneratedImagesStore();

  const allTyped = useMemo(() => {
    const base = store.orderIds
      .map((id) => store.byId[id])
      .filter(Boolean) as GeneratedImage[];

    // Pipeline filter takes priority — returns only images from that specific flow
    if (pipeline) return base.filter((img) => img.pipeline === pipeline);

    if (!generationType || generationType === "all") return base;
    if (generationType === "ad") return base.filter((img) => img.pipeline === "ad");
    if (generationType === "edit") return base.filter((img) => img.pipeline === "edit");
    // "images": both ad and edit
    return base.filter((img) => img.pipeline === "ad" || img.pipeline === "edit");
  }, [store.byId, store.orderIds, generationType, pipeline]);

  const rawPendingImages = useMemo(
    () => allTyped.filter((img) => img.status === "pending" || img.status === "processing"),
    [allTyped]
  );

  const getImageById = useCallback(
    (id: string) => store.byId[id] as GeneratedImage | undefined,
    [store.byId]
  );

  const { completingIds, pendingItems: pendingImages } = useCompletionAnimation({
    rawPendingItems: rawPendingImages,
    getItemById: getImageById,
  });

  // Get completed images for display (exclude items in completion animation)
  const images = useMemo(() => {
    const completed = allTyped.filter(
      (img) => img.status === "completed" && !completingIds.has(img.id)
    );

    // Precompute each batch's newest timestamp so cross-batch comparison is stable
    const batchMaxTime = new Map<string, number>();
    for (const img of completed) {
      const key = img.batch_id ?? img.id;
      const t = new Date(img.created_at).getTime();
      batchMaxTime.set(key, Math.max(batchMaxTime.get(key) ?? 0, t));
    }

    // Sort: same-batch images stay adjacent, newest batch first
    return [...completed].sort((a, b) => {
      const aBatch = a.batch_id ?? a.id;
      const bBatch = b.batch_id ?? b.id;
      if (aBatch === bBatch) {
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      }
      return (batchMaxTime.get(bBatch) ?? 0) - (batchMaxTime.get(aBatch) ?? 0);
    });
  }, [allTyped, completingIds]);

  // Auto-fail stale pending images (Realtime subscription handles UI update)
  useStaleGenerationCleanup({
    pendingItems: rawPendingImages,
    table: "generated_images",
  });

  const failedImages = useMemo(
    () => allTyped.filter((img) => img.status === "failed"),
    [allTyped]
  );

  return {
    images,
    pendingImages,
    pendingCount: pendingImages.length,
    completingIds,
    failedImages,
    failedCount: failedImages.length,
    loading: store.loading,
    error: store.error,
    refetch: store.refetch,
  };
}
